import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import * as newsApi from '@app/api/news.api';
import { News, NewsFilters, NewsPaginatedResponse } from '@app/types/api.types';

export interface NewsState {
  items: News[];
  currentItem: News | null;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
  filters: NewsFilters;
  loading: boolean;
  error: string | null;
}

const initialState: NewsState = {
  items: [],
  currentItem: null,
  pagination: {
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
    hasNext: false,
    hasPrev: false,
  },
  filters: {
    page: 1,
    limit: 10,
  },
  loading: false,
  error: null,
};

export const fetchNews = createAsyncThunk(
  'news/fetchNews',
  async (filters: NewsFilters = { page: 1, limit: 10 }, { rejectWithValue }) => {
    try {
      return await newsApi.getNews(filters);
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch news');
    }
  }
);

export const fetchNewsById = createAsyncThunk(
  'news/fetchNewsById',
  async (id: string, { rejectWithValue }) => {
    try {
      return await newsApi.getNewsById(id);
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch news');
    }
  }
);

export const createNews = createAsyncThunk(
  'news/createNews',
  async (data: Parameters<typeof newsApi.createNews>[0], { rejectWithValue }) => {
    try {
      return await newsApi.createNews(data);
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to create news');
    }
  }
);

export const updateNews = createAsyncThunk(
  'news/updateNews',
  async ({ id, data }: { id: string; data: Parameters<typeof newsApi.updateNews>[1] }, { rejectWithValue }) => {
    try {
      return await newsApi.updateNews(id, data);
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to update news');
    }
  }
);

export const deleteNews = createAsyncThunk(
  'news/deleteNews',
  async (id: string, { rejectWithValue }) => {
    try {
      await newsApi.deleteNews(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to delete news');
    }
  }
);

const newsSlice = createSlice({
  name: 'news',
  initialState,
  reducers: {
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearCurrentItem: (state) => {
      state.currentItem = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch News
    builder.addCase(fetchNews.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchNews.fulfilled, (state, action) => {
      state.loading = false;
      state.items = action.payload.data;
      state.pagination = {
        page: action.payload.page,
        limit: action.payload.limit,
        total: action.payload.total,
        totalPages: action.payload.totalPages,
        hasNext: action.payload.hasNext,
        hasPrev: action.payload.hasPrev,
      };
    });
    builder.addCase(fetchNews.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Fetch News By ID
    builder.addCase(fetchNewsById.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchNewsById.fulfilled, (state, action) => {
      state.loading = false;
      state.currentItem = action.payload;
    });
    builder.addCase(fetchNewsById.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Create News
    builder.addCase(createNews.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(createNews.fulfilled, (state, action) => {
      state.loading = false;
      state.items.unshift(action.payload);
    });
    builder.addCase(createNews.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Update News
    builder.addCase(updateNews.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(updateNews.fulfilled, (state, action) => {
      state.loading = false;
      const index = state.items.findIndex((item) => item.id === action.payload.id);
      if (index !== -1) {
        state.items[index] = action.payload;
      }
      if (state.currentItem?.id === action.payload.id) {
        state.currentItem = action.payload;
      }
    });
    builder.addCase(updateNews.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Delete News
    builder.addCase(deleteNews.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(deleteNews.fulfilled, (state, action) => {
      state.loading = false;
      state.items = state.items.filter((item) => item.id !== action.payload);
      if (state.currentItem?.id === action.payload) {
        state.currentItem = null;
      }
    });
    builder.addCase(deleteNews.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });
  },
});

export const { setFilters, clearCurrentItem, clearError } = newsSlice.actions;
export default newsSlice.reducer;

